import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

// Smooth scroll function
const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    const offset = 80;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }
};

// Custom cursor component
const CustomCursor = () => {
  const cursorRef = useRef(null);

  useEffect(() => {
    const cursor = cursorRef.current;
    if (!cursor) return;

    const moveCursor = (e) => {
      cursor.style.left = e.clientX + 'px';
      cursor.style.top = e.clientY + 'px';
    };

    const addHoverEffect = () => {
      cursor.style.transform = 'scale(1.5)';
      cursor.style.border = '2px solid #3b82f6';
    };

    const removeHoverEffect = () => {
      cursor.style.transform = 'scale(1)';
      cursor.style.border = '2px solid rgba(59, 130, 246, 0.5)';
    };

    document.addEventListener('mousemove', moveCursor);
    document.querySelectorAll('a, button, .cursor-hover').forEach(el => {
      el.addEventListener('mouseenter', addHoverEffect);
      el.addEventListener('mouseleave', removeHoverEffect);
    });

    return () => {
      document.removeEventListener('mousemove', moveCursor);
    };
  }, []);

  return (
    <div
      ref={cursorRef}
      className="fixed w-6 h-6 border-2 border-blue-500/50 rounded-full pointer-events-none z-50 transition-all duration-200 ease-out mix-blend-difference"
      style={{ transform: 'translate(-50%, -50%)' }}
    />
  );
};

// Sidebar Navigation Component
const SidebarNavigation = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        'hero', 'about', 'skills', 'languages', 'certifications',
        'experience', 'volunteering', 'projects', 'personal-note'
      ];

      const current = sections.find(sectionId => {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });

      if (current) {
        setActiveSection(current);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigationItems = [
    { id: 'hero', label: 'Home', color: 'from-blue-500 to-purple-500' },
    { id: 'about', label: 'About', color: 'from-blue-500 to-purple-500' },
    { id: 'skills', label: 'Skills', color: 'from-green-500 to-blue-500' },
    { id: 'languages', label: 'Languages', color: 'from-yellow-500 to-orange-500' },
    { id: 'certifications', label: 'Certifications', color: 'from-purple-500 to-pink-500' },
    { id: 'experience', label: 'Experience', color: 'from-indigo-500 to-blue-500' },
    { id: 'volunteering', label: 'Community', color: 'from-green-500 to-teal-500' },
    { id: 'projects', label: 'Projects', color: 'from-orange-500 to-red-500' },
    { id: 'personal-note', label: 'Personal', color: 'from-pink-500 to-purple-500' }
  ];

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <div 
          className={`fixed left-3 top-1/2 transform -translate-y-1/2 z-40 transition-all duration-300 ${
            isHovered ? 'w-32' : 'w-8'
          }`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="glass-dark rounded-xl p-2 backdrop-blur-xl border border-blue-500/20 shadow-2xl">
            <div className="space-y-1 flex flex-col items-center">
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`group relative w-full flex flex-col items-center justify-center transition-all duration-300 rounded-lg p-1.5 ${
                    activeSection === item.id 
                      ? 'bg-gradient-to-r ' + item.color + ' text-white shadow-lg' 
                      : 'text-gray-300 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {/* Icon Placeholder - Much Smaller Circle */}
                  <div className={`flex-shrink-0 w-3 h-3 rounded-full transition-all duration-300 ${
                    activeSection === item.id 
                      ? 'bg-white/20' 
                      : 'bg-gradient-to-r ' + item.color + ' opacity-60'
                  }`}></div>
                  
                  {/* Label - appears on hover, centered */}
                  <span className={`mt-1 text-xs font-medium transition-all duration-300 text-center ${
                    isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'
                  }`}>
                    {item.label}
                  </span>
                  
                  {/* Active indicator */}
                  {activeSection === item.id && (
                    <div className="absolute -right-1 top-1/2 transform -translate-y-1/2 w-1 h-1 bg-white rounded-full animate-pulse"></div>
                  )}
                  
                  {/* Hover background effect */}
                  <div className={`absolute inset-0 rounded-lg transition-all duration-300 ${
                    isHovered && activeSection !== item.id 
                      ? 'bg-gradient-to-r ' + item.color + ' opacity-15' 
                      : 'opacity-0'
                  }`}></div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="lg:hidden fixed bottom-4 left-1/2 transform -translate-x-1/2 z-40">
        <div className="glass-dark rounded-2xl px-4 py-3 backdrop-blur-xl border border-blue-500/20 shadow-2xl">
          <div className="flex items-center space-x-2">
            {navigationItems.slice(0, 5).map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`group relative flex flex-col items-center transition-all duration-300 rounded-xl p-2 min-w-[3rem] ${
                  activeSection === item.id 
                    ? 'bg-gradient-to-r ' + item.color + ' text-white shadow-lg' 
                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                }`}
              >
                {/* Icon Placeholder - Better sized for mobile */}
                <div className={`w-3 h-3 rounded-full mb-1.5 transition-all duration-300 ${
                  activeSection === item.id 
                    ? 'bg-white/20' 
                    : 'bg-gradient-to-r ' + item.color + ' opacity-60'
                }`}></div>
                
                {/* Label */}
                <span className="text-xs font-medium leading-tight">
                  {item.label}
                </span>
                
                {/* Active indicator */}
                {activeSection === item.id && (
                  <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-white rounded-full animate-pulse"></div>
                )}
              </button>
            ))}
            
            {/* More button for additional sections */}
            <button
              onClick={() => scrollToSection('projects')}
              className="group relative flex flex-col items-center transition-all duration-300 rounded-xl p-2 min-w-[3rem] text-gray-300 hover:text-white hover:bg-white/10"
            >
              <div className="w-3 h-3 rounded-full mb-1.5 bg-gradient-to-r from-orange-500 to-red-500 opacity-60"></div>
              <span className="text-xs font-medium leading-tight">More</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

function Home() {
  const [homepageData, setHomepageData] = useState(null);
  const [loading, setLoading] = useState(true);



  useEffect(() => {
    const fetchHomepageData = async () => {
      try {
        const homepageRef = doc(db, "homepage", "main");
        const homepageSnap = await getDoc(homepageRef);
        if (homepageSnap.exists()) {
          setHomepageData(homepageSnap.data());
        }
      } catch (error) {
        console.error("Error fetching homepage data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHomepageData();
  }, []);

  // Additional scroll reset for this component
  useEffect(() => {
    // Force scroll to top when this component mounts
    window.scrollTo(0, 0);
    
    // Also reset on document elements
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-white text-lg font-medium">Loading Portfolio...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-hero">
      <CustomCursor />
      <SidebarNavigation />
      
      {/* Hero Section */}
      <section id="hero" className="min-h-screen flex items-center justify-center relative overflow-hidden px-4 sm:px-6">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/30 to-purple-900/30"></div>
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-48 h-48 sm:w-72 sm:h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute top-40 right-20 w-48 h-48 sm:w-72 sm:h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-40 w-48 h-48 sm:w-72 sm:h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>
        
                <div className="relative z-10 text-center max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-4 sm:mb-6 animate-fade-in-up leading-tight">
            {homepageData?.hero?.title || "Mohammed Kamal Ali"}
              </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-blue-200 mb-6 sm:mb-8 animate-fade-in-up animation-delay-200 leading-relaxed px-2">
            {homepageData?.hero?.subtitle || "Undergraduate in Computer Networks & Security"}<br className="hidden sm:block"/>
            {homepageData?.hero?.faculty || "Faculty of Engineering and Computer Science"}
            <br className="hidden sm:block"/>
            <span className="text-purple-200 font-semibold">{homepageData?.hero?.tagline || "Critical Thinker | Lifelong Learner"}</span>
          </p>
          <p className="text-base sm:text-lg text-gray-300 mb-8 sm:mb-12 max-w-3xl mx-auto animate-fade-in-up animation-delay-400 px-4">
            {homepageData?.hero?.description || "I'm not just learning technologies — I'm learning how to think, adapt, and solve real-world problems."}
          </p>
          
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center animate-fade-in-up animation-delay-600 px-4">
            {homepageData?.hero?.buttons?.map((button, index) => {
              if (button.type === 'primary') {
                return (
                  <Link
                    key={index}
                    to={button.link}
                    className="btn-primary cursor-hover text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4"
                  >
                    {button.text}
                  </Link>
                );
              } else if (button.type === 'secondary' && button.action === 'scroll') {
                return (
                  <button
                    key={index}
                    onClick={() => scrollToSection(button.target)}
                    className="btn-secondary cursor-hover text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4"
                  >
                    {button.text}
                  </button>
                );
              } else if (button.type === 'tertiary') {
                return (
                  <a
                    key={index}
                    href={homepageData?.resume?.filePath || button.link}
                    download={homepageData?.resume?.fileName || "Mohammed_Kamal_Ali_Resume.pdf"}
                    className="px-6 sm:px-8 py-3 sm:py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-300 transform hover:scale-105 cursor-hover text-sm sm:text-base"
                  >
                    {button.text}
                  </a>
                );
              }
              return null;
            })}
              </div>
            </div>
      </section>

      {/* About Me Section */}
      <section id="about" className="py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-12 sm:mb-16">{homepageData?.about?.title || "About Me"}</h2>
          <div className="glass-dark rounded-2xl p-6 sm:p-8">
            {homepageData?.about?.content?.map((paragraph, index) => (
              <p key={index} className={`text-base sm:text-lg text-gray-300 leading-relaxed ${index < homepageData.about.content.length - 1 ? 'mb-4 sm:mb-6' : ''}`}>
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </section>

            {/* Skills Section */}
      <section id="skills" className="py-16 sm:py-20 px-4 sm:px-6 bg-slate-800/20">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-12 sm:mb-16">{homepageData?.skills?.title || "Skills & Expertise"}</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
            {homepageData?.skills?.categories?.map((category, index) => (
              <div key={index} className="space-y-4 sm:gap-6">
                <div className="glass-dark rounded-xl p-4 sm:p-6 card-hover">
                  <h3 className="text-lg sm:text-xl font-semibold text-blue-400 mb-3 sm:mb-4">{category.name}</h3>
                  <ul className="space-y-2 text-sm sm:text-base text-gray-300">
                    {category.items?.map((item, itemIndex) => (
                      <li key={itemIndex}>• {item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* Core Competencies */}
          <div className="mt-8 sm:mt-12 glass-dark rounded-xl p-6 sm:p-8">
            <h3 className="text-xl sm:text-2xl font-semibold text-blue-400 mb-4 sm:mb-6 text-center">Core Competencies</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {homepageData?.skills?.competencies?.map((skill, index) => (
                <div key={index} className="text-center p-3 sm:p-4 bg-blue-500/10 rounded-lg border border-blue-500/20 card-hover">
                  <p className="text-sm sm:text-base text-gray-300 font-medium leading-tight">{skill}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Languages Section */}
      <section id="languages" className="py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-12 sm:mb-16">{homepageData?.languages?.title || "Languages"}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
            {homepageData?.languages?.items?.map((lang, index) => (
              <div key={index} className="glass-dark rounded-xl p-4 sm:p-6 text-center card-hover">
                <h3 className="text-lg sm:text-xl font-semibold text-blue-400 mb-2">{lang.language}</h3>
                <p className="text-sm sm:text-base text-gray-300">{lang.level}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section id="certifications" className="py-16 sm:py-20 px-4 sm:px-6 bg-slate-800/20">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-12 sm:mb-16">{homepageData?.certifications?.title || "Certifications & Achievements"}</h2>
          {homepageData?.certifications?.items?.map((cert, index) => (
            <div key={index} className="glass-dark rounded-2xl p-6 sm:p-8">
              <h3 className="text-xl sm:text-2xl font-semibold text-blue-400 mb-3 sm:mb-4">{cert.name}</h3>
              <p className="text-base sm:text-lg text-gray-300 mb-3 sm:mb-4">{cert.subtitle}</p>
              <ul className="space-y-2 text-sm sm:text-base text-gray-300">
                {cert.details?.map((detail, detailIndex) => (
                  <li key={detailIndex}>• {detail}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-12 sm:mb-16">{homepageData?.experience?.title || "Experience Highlights"}</h2>
          <div className="space-y-4 sm:space-y-6">
            {homepageData?.experience?.items?.map((exp, index) => (
              <div key={index} className="glass-dark rounded-xl p-4 sm:p-6 card-hover">
                <p className="text-base sm:text-lg text-gray-300">{exp}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Volunteering Section */}
      <section id="volunteering" className="py-16 sm:py-20 px-4 sm:px-6 bg-slate-800/20">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-12 sm:mb-16">{homepageData?.volunteering?.title || "Volunteering & Community"}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            {homepageData?.volunteering?.items?.map((vol, index) => (
              <div key={index} className="glass-dark rounded-xl p-4 sm:p-6 text-center card-hover">
                <p className="text-sm sm:text-base text-gray-300">{vol}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-12 sm:mb-16">{homepageData?.projects?.title || "General Projects"}</h2>
          <p className="text-center text-sm sm:text-base text-gray-300 mb-8 sm:mb-12 px-4">
            {homepageData?.projects?.description || "These are projects I've completed alongside my academic coursework. They represent my practical skills and how I apply what I learn in real-world scenarios."}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {homepageData?.projects?.items?.map((project, index) => (
              <div key={index} className="glass-dark rounded-xl p-4 sm:p-6 card-hover cursor-hover">
                <h3 className="text-lg sm:text-xl font-semibold text-blue-400 mb-2 sm:mb-3">{project.title}</h3>
                <p className="text-sm sm:text-base text-gray-300 mb-2 sm:mb-3">{project.description}</p>
                <span className="inline-block px-2 sm:px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs sm:text-sm">
                  {project.tech}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Personal Note Section */}
      <section id="personal-note" className="py-16 sm:py-20 px-4 sm:px-6 bg-slate-800/20">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-12 sm:mb-16">{homepageData?.personalNote?.title || "Personal Note"}</h2>
          <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-blue-500/20">
            <blockquote className="text-lg sm:text-xl text-gray-300 italic text-center leading-relaxed">
              "{homepageData?.personalNote?.quote || "I don't believe in just memorizing courses — I believe in understanding, applying, and learning continuously. I'm not an expert in everything, but I am confident in my ability to learn fast, adapt, and solve problems when they come. That's what makes me excited about Computer Networks & Security."}"
            </blockquote>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 bg-slate-900/40 border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm sm:text-base text-gray-400 mb-4">
            {homepageData?.footer?.description || "Ready to connect? Let's discuss how I can contribute to your team."}
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-6">
            {homepageData?.footer?.links?.map((link, index) => (
              <a key={index} href={link.url} className="text-gray-400 hover:text-blue-400 transition-colors duration-300 text-sm sm:text-base">
                {link.text}
              </a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;
